import { useEffect, useState, useCallback, useRef } from "react";
import { ops, apiError, todayISO, fmtDate, fmtDT } from "./opsApi";
import { inputCls, Field, Btn, Badge, Card, CardTitle, StatCard, TabBar, Empty, Msg } from "./opsUi";

const useMsg = () => {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  const flash = (ok, text) => {
    if (timer.current) clearTimeout(timer.current);
    setMsg({ ok, text });
    timer.current = setTimeout(() => setMsg(null), 4000);
  };
  return [msg, flash];
};

const EvalText = ({ ev }) => ev ? (
  <div className="mt-1 space-y-0.5 text-[11px] font-semibold text-dwc-charcoal/70">
    <p>Confidence: <b>{ev.confidence}</b> · Panic: <b>{ev.panic}</b> · Awareness: <b>{String(ev.road_awareness).replace("_", " & ")}</b></p>
    {ev.repeated_mistakes && <p>Repeated mistakes: {ev.repeated_mistakes}</p>}
    {ev.remarks && <p>Remarks: {ev.remarks}</p>}
    {ev.next_focus && <p>Next focus: {ev.next_focus}</p>}
  </div>
) : null;

/* ---------- Overview ---------- */
const Overview = () => {
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  const [date, setDate] = useState(todayISO());
  useEffect(() => { ops.get("/reports/overview").then(setData).catch(() => {}); }, []);
  useEffect(() => { ops.get("/reports/daily", { date }).then(setDaily).catch(() => {}); }, [date]);
  if (!data) return <Empty>Loading overview...</Empty>;
  const c = data.counts;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <StatCard label="Trainers" value={c.trainers} testId="ov-trainers" />
        <StatCard label="Students" value={c.students} testId="ov-students" />
        <StatCard label="Sessions Today" value={c.sessions_today} testId="ov-sessions-today" />
        <StatCard label="Upcoming" value={c.scheduled_upcoming} testId="ov-upcoming" />
        <StatCard label="Open Incidents" value={c.open_incidents} testId="ov-incidents" />
        <StatCard label="Pending Leaves" value={c.pending_leaves} testId="ov-leaves" />
        <StatCard label="Pending Tasks" value={c.pending_tasks} testId="ov-tasks" />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Card testId="ov-student-progress">
          <CardTitle>Student Progress (completed / package)</CardTitle>
          {data.student_progress.length === 0 && <Empty>No students yet.</Empty>}
          <div className="space-y-2.5">
            {data.student_progress.map((p) => (
              <div key={p.student_id} className="flex items-center gap-3">
                <span className="w-32 truncate text-sm font-bold text-dwc-charcoal">{p.name}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-dwc-green" style={{ width: `${p.total ? Math.min(100, (p.completed / p.total) * 100) : 0}%` }} />
                </div>
                <span className="text-xs font-extrabold text-dwc-charcoal/60">{p.completed}/{p.total || "—"} <span className="text-dwc-charcoal/40">({p.remaining} left)</span></span>
              </div>
            ))}
          </div>
        </Card>
        <Card testId="ov-trainer-attendance">
          <CardTitle>Trainer Attendance & Activity (today)</CardTitle>
          {data.trainer_attendance.length === 0 && <Empty>No trainers yet.</Empty>}
          <div className="space-y-2">
            {data.trainer_attendance.map((t) => (
              <div key={t.trainer_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-dwc-soft/60 px-3 py-2">
                <span className="text-sm font-bold text-dwc-charcoal">{t.name}</span>
                <span className="flex items-center gap-2 text-[11px] font-bold text-dwc-charcoal/60">
                  {t.on_leave_today ? <Badge value="pending" /> : <Badge value={t.sessions_today > 0 ? "scheduled" : "done"} />}
                  {t.on_leave_today ? "On leave" : `${t.completed_today}/${t.sessions_today} today`} · {t.total_completed} total
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card testId="ov-daily-report">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Daily Operations Report</CardTitle>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} max-w-[170px]`} data-testid="daily-report-date" />
        </div>
        {!daily || daily.sessions.length === 0 ? <Empty>No sessions on {fmtDate(date)}.</Empty> : (
          <div className="space-y-3">
            {daily.sessions.map((s) => (
              <div key={s.id} className="rounded-xl border border-gray-100 p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-dwc-charcoal">
                  {s.time} · {s.student_name} <span className="text-dwc-charcoal/40">with</span> {s.trainer_name} <Badge value={s.status} />
                </div>
                <EvalText ev={s.evaluation} />
              </div>
            ))}
          </div>
        )}
        {daily && daily.incidents.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <p className="text-[11px] font-extrabold uppercase text-dwc-charcoal/50">Incidents reported this day</p>
            {daily.incidents.map((i) => (
              <p key={i.id} className="mt-1 text-xs font-semibold text-dwc-charcoal/70">• [{i.type}] {i.description} — {i.reporter_name}</p>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

/* ---------- Users ---------- */
const UserForm = ({ trainers, onSaved, flash }) => {
  const blank = { name: "", mobile: "", password: "", role: "student", package_name: "", package_total: 0, trainer_id: "", email: "", notes: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await ops.post("/users", { ...f, package_total: Number(f.package_total) || 0 });
      flash(true, `${f.role.replace("_", " ")} "${f.name}" created`);
      setF(blank);
      onSaved();
    } catch (err) { flash(false, apiError(err)); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="user-create-form">
      <Field label="Role">
        <select value={f.role} onChange={set("role")} className={inputCls} data-testid="user-role-select">
          <option value="student">Student</option>
          <option value="trainer">Trainer</option>
          <option value="service_manager">Service Manager</option>
        </select>
      </Field>
      <Field label="Full name"><input value={f.name} onChange={set("name")} className={inputCls} required data-testid="user-name-input" /></Field>
      <Field label="Mobile (login ID)"><input value={f.mobile} onChange={set("mobile")} className={inputCls} required data-testid="user-mobile-input" /></Field>
      <Field label="First password"><input value={f.password} onChange={set("password")} className={inputCls} required minLength={6} data-testid="user-password-input" /></Field>
      {f.role === "student" && (
        <>
          <Field label="Package name"><input value={f.package_name} onChange={set("package_name")} className={inputCls} placeholder="e.g. 33-Lesson Complete" data-testid="user-package-name" /></Field>
          <Field label="Total package sessions"><input type="number" min="0" value={f.package_total} onChange={set("package_total")} className={inputCls} data-testid="user-package-total" /></Field>
          <Field label="Assign trainer">
            <select value={f.trainer_id} onChange={set("trainer_id")} className={inputCls} data-testid="user-trainer-select">
              <option value="">— None yet —</option>
              {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
        </>
      )}
      <div className="flex items-end"><Btn type="submit" testId="user-create-btn">Add User</Btn></div>
    </form>
  );
};

const Users = ({ isAdmin }) => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [msg, flash] = useMsg();
  const load = useCallback(() => ops.get("/users").then(setUsers).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const trainers = users.filter((u) => u.role === "trainer" && u.active !== false);
  const shown = filter ? users.filter((u) => u.role === filter) : users;

  const update = async (id, body, okText) => {
    try { await ops.put(`/users/${id}`, body); flash(true, okText); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await ops.del(`/users/${u.id}`); flash(true, "User deleted"); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const resetPassword = (u) => {
    const p = window.prompt(`New password for ${u.name} (min 6 chars):`);
    if (p && p.length >= 6) update(u.id, { password: p }, "Password updated");
  };

  return (
    <div className="space-y-5">
      <Msg msg={msg} />
      {isAdmin && <Card testId="users-create-card"><CardTitle>Add Trainer / Student / Service Manager (Admin only)</CardTitle><UserForm trainers={trainers} onSaved={load} flash={flash} /></Card>}
      <Card testId="users-list-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>All Users ({shown.length})</CardTitle>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="users-filter">
            <option value="">All roles</option><option value="student">Students</option><option value="trainer">Trainers</option><option value="service_manager">Service Managers</option>
          </select>
        </div>
        {shown.length === 0 && <Empty>No users yet. {isAdmin ? "Add your first trainer or student above." : ""}</Empty>}
        <div className="space-y-2.5">
          {shown.map((u) => (
            <div key={u.id} className="rounded-xl border border-gray-100 p-3" data-testid={`user-row-${u.mobile}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-black text-dwc-charcoal">{u.name} {u.active === false && <span className="text-[10px] font-bold text-red-500">(deactivated)</span>}</p>
                  <p className="text-[11px] font-bold text-dwc-charcoal/55">
                    {u.role.replace("_", " ")} · {u.mobile}
                    {u.role === "student" && <> · {u.completed_sessions}/{u.package_total || "—"} sessions · Trainer: {u.trainer_name || "none"}</>}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {u.role === "student" && (
                    <select value={u.trainer_id || ""} onChange={(e) => update(u.id, { trainer_id: e.target.value }, "Trainer assigned")}
                      className="rounded-full border border-dwc-green/25 bg-white px-3 py-1.5 text-[11px] font-extrabold text-dwc-green" data-testid={`assign-trainer-${u.mobile}`}>
                      <option value="">Assign trainer...</option>
                      {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                  {isAdmin && (
                    <>
                      <Btn small variant="outline" onClick={() => resetPassword(u)} testId={`reset-pass-${u.mobile}`}>Reset Pass</Btn>
                      <Btn small variant="outline" onClick={() => update(u.id, { active: u.active === false }, u.active === false ? "User activated" : "User deactivated")} testId={`toggle-active-${u.mobile}`}>
                        {u.active === false ? "Activate" : "Deactivate"}
                      </Btn>
                      <Btn small variant="danger" onClick={() => remove(u)} testId={`delete-user-${u.mobile}`}>Delete</Btn>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

/* ---------- Schedule ---------- */
const Schedule = () => {
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [date, setDate] = useState("");
  const [f, setF] = useState({ student_id: "", trainer_id: "", date: todayISO(), time: "10:00", duration_min: 60, note: "" });
  const [msg, flash] = useMsg();
  const load = useCallback(() => ops.get("/sessions", date ? { date } : {}).then(setSessions).catch(() => {}), [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { ops.get("/users").then(setUsers).catch(() => {}); }, []);
  const students = users.filter((u) => u.role === "student" && u.active !== false);
  const trainers = users.filter((u) => u.role === "trainer" && u.active !== false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await ops.post("/sessions", { ...f, duration_min: Number(f.duration_min) || 60 });
      flash(true, "Session scheduled");
      load();
    } catch (err) { flash(false, apiError(err)); }
  };
  const update = async (id, body, okText) => {
    try { await ops.put(`/sessions/${id}`, body); flash(true, okText); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const reschedule = (s) => {
    const nd = window.prompt("New date (YYYY-MM-DD):", s.date);
    if (!nd) return;
    const nt = window.prompt("New time (HH:MM):", s.time);
    if (!nt) return;
    update(s.id, { date: nd, time: nt, status: "scheduled" }, "Session rescheduled");
  };

  return (
    <div className="space-y-5">
      <Msg msg={msg} />
      <Card testId="schedule-create-card">
        <CardTitle>Schedule a Session</CardTitle>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="session-create-form">
          <Field label="Student">
            <select value={f.student_id} onChange={set("student_id")} className={inputCls} required data-testid="session-student-select">
              <option value="">Select student...</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Trainer (blank = assigned)">
            <select value={f.trainer_id} onChange={set("trainer_id")} className={inputCls} data-testid="session-trainer-select">
              <option value="">Student's trainer</option>
              {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Date"><input type="date" value={f.date} onChange={set("date")} className={inputCls} required data-testid="session-date-input" /></Field>
          <Field label="Time"><input type="time" value={f.time} onChange={set("time")} className={inputCls} required data-testid="session-time-input" /></Field>
          <div className="flex items-end"><Btn type="submit" testId="session-create-btn">Schedule</Btn></div>
        </form>
      </Card>
      <Card testId="schedule-list-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Sessions {date ? `on ${fmtDate(date)}` : "(all recent)"}</CardTitle>
          <div className="flex gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} max-w-[170px]`} data-testid="schedule-filter-date" />
            {date && <Btn small variant="outline" onClick={() => setDate("")}>Clear</Btn>}
          </div>
        </div>
        {sessions.length === 0 && <Empty>No sessions found.</Empty>}
        <div className="space-y-2.5">
          {sessions.map((s) => (
            <div key={s.id} className="rounded-xl border border-gray-100 p-3" data-testid={`session-row-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-black text-dwc-charcoal">{fmtDate(s.date)} · {s.time} <Badge value={s.status} /></p>
                  <p className="text-[11px] font-bold text-dwc-charcoal/55">{s.student_name} with {s.trainer_name} · {s.duration_min} min {s.note && `· ${s.note}`}</p>
                  <EvalText ev={s.evaluation} />
                </div>
                {["scheduled"].includes(s.status) && (
                  <div className="flex flex-wrap gap-1.5">
                    <Btn small variant="outline" onClick={() => reschedule(s)} testId={`reschedule-${s.id}`}>Reschedule</Btn>
                    <select defaultValue="" onChange={(e) => e.target.value && update(s.id, { trainer_id: e.target.value }, "Replacement trainer set")}
                      className="rounded-full border border-dwc-green/25 bg-white px-3 py-1.5 text-[11px] font-extrabold text-dwc-green" data-testid={`replace-trainer-${s.id}`}>
                      <option value="">Replace trainer...</option>
                      {trainers.filter((t) => t.id !== s.trainer_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <Btn small variant="outline" onClick={() => update(s.id, { status: "missed" }, "Marked missed")} testId={`miss-${s.id}`}>Missed</Btn>
                    <Btn small variant="danger" onClick={() => update(s.id, { status: "cancelled" }, "Session cancelled")} testId={`cancel-${s.id}`}>Cancel</Btn>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

/* ---------- Leaves ---------- */
const Leaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [f, setF] = useState({ trainer_id: "", date_from: todayISO(), date_to: todayISO(), reason: "" });
  const [msg, flash] = useMsg();
  const load = useCallback(() => ops.get("/leaves").then(setLeaves).catch(() => {}), []);
  useEffect(() => { load(); ops.get("/users", { role: "trainer" }).then(setTrainers).catch(() => {}); }, [load]);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const create = async (e) => {
    e.preventDefault();
    try { await ops.post("/leaves", f); flash(true, "Leave recorded"); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const decide = async (id, status) => {
    try { await ops.put(`/leaves/${id}`, { status }); flash(true, `Leave ${status}`); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  return (
    <div className="space-y-5">
      <Msg msg={msg} />
      <Card testId="leave-create-card">
        <CardTitle>Record Trainer Leave</CardTitle>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="leave-create-form">
          <Field label="Trainer">
            <select value={f.trainer_id} onChange={set("trainer_id")} className={inputCls} required data-testid="leave-trainer-select">
              <option value="">Select trainer...</option>
              {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="From"><input type="date" value={f.date_from} onChange={set("date_from")} className={inputCls} required /></Field>
          <Field label="To"><input type="date" value={f.date_to} onChange={set("date_to")} className={inputCls} required /></Field>
          <Field label="Reason"><input value={f.reason} onChange={set("reason")} className={inputCls} required minLength={3} data-testid="leave-reason-input" /></Field>
          <div className="flex items-end"><Btn type="submit" testId="leave-create-btn">Add Leave</Btn></div>
        </form>
      </Card>
      <Card testId="leaves-list-card">
        <CardTitle>Leave Requests</CardTitle>
        {leaves.length === 0 && <Empty>No leave records.</Empty>}
        <div className="space-y-2.5">
          {leaves.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 p-3" data-testid={`leave-row-${l.id}`}>
              <div>
                <p className="text-sm font-black text-dwc-charcoal">{l.trainer_name} <Badge value={l.status} /></p>
                <p className="text-[11px] font-bold text-dwc-charcoal/55">{fmtDate(l.date_from)} → {fmtDate(l.date_to)} · {l.reason}</p>
              </div>
              {l.status === "pending" && (
                <div className="flex gap-1.5">
                  <Btn small onClick={() => decide(l.id, "approved")} testId={`approve-leave-${l.id}`}>Approve</Btn>
                  <Btn small variant="danger" onClick={() => decide(l.id, "rejected")} testId={`reject-leave-${l.id}`}>Reject</Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

/* ---------- Incidents ---------- */
const Incidents = () => {
  const [items, setItems] = useState([]);
  const [msg, flash] = useMsg();
  const load = useCallback(() => ops.get("/incidents").then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const update = async (id, body, okText) => {
    try { await ops.put(`/incidents/${id}`, body); flash(true, okText); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const resolve = (i) => {
    const r = window.prompt("Resolution note:", i.resolution || "");
    if (r !== null) update(i.id, { status: "resolved", resolution: r }, "Incident resolved");
  };
  return (
    <Card testId="incidents-card">
      <CardTitle>Complaints, Damage & Safety Incidents</CardTitle>
      <Msg msg={msg} />
      {items.length === 0 && <Empty>No incidents reported.</Empty>}
      <div className="space-y-2.5">
        {items.map((i) => (
          <div key={i.id} className="rounded-xl border border-gray-100 p-3" data-testid={`incident-row-${i.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-black text-dwc-charcoal">
                  {i.type.replace(/_/g, " ").toUpperCase()} <Badge value={i.status} />
                </p>
                <p className="text-xs font-semibold text-dwc-charcoal/70">{i.description}</p>
                <p className="mt-0.5 text-[10px] font-bold text-dwc-charcoal/45">By {i.reporter_name} ({i.reporter_role}) · {fmtDT(i.created_at)}</p>
                {i.resolution && <p className="mt-0.5 text-[11px] font-bold text-emerald-700">Resolution: {i.resolution}</p>}
              </div>
              {i.status !== "resolved" && (
                <div className="flex gap-1.5">
                  {i.status === "open" && <Btn small variant="outline" onClick={() => update(i.id, { status: "in_review" }, "Marked in review")} testId={`review-${i.id}`}>In Review</Btn>}
                  <Btn small onClick={() => resolve(i)} testId={`resolve-${i.id}`}>Resolve</Btn>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

/* ---------- Tasks ---------- */
const Tasks = () => {
  const [items, setItems] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [f, setF] = useState({ title: "", assigned_to: "", due_date: "", details: "" });
  const [msg, flash] = useMsg();
  const load = useCallback(() => ops.get("/tasks").then(setItems).catch(() => {}), []);
  useEffect(() => { load(); ops.get("/users", { role: "trainer" }).then(setTrainers).catch(() => {}); }, [load]);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const create = async (e) => {
    e.preventDefault();
    try { await ops.post("/tasks", f); flash(true, "Task assigned"); setF({ title: "", assigned_to: "", due_date: "", details: "" }); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const toggle = async (t) => {
    try { await ops.put(`/tasks/${t.id}`, { status: t.status === "done" ? "pending" : "done" }); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  return (
    <div className="space-y-5">
      <Msg msg={msg} />
      <Card testId="task-create-card">
        <CardTitle>Assign Trainer Task</CardTitle>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="task-create-form">
          <Field label="Task"><input value={f.title} onChange={set("title")} className={inputCls} required data-testid="task-title-input" /></Field>
          <Field label="Assign to">
            <select value={f.assigned_to} onChange={set("assigned_to")} className={inputCls} required data-testid="task-trainer-select">
              <option value="">Select trainer...</option>
              {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Due date"><input type="date" value={f.due_date} onChange={set("due_date")} className={inputCls} /></Field>
          <div className="flex items-end"><Btn type="submit" testId="task-create-btn">Assign</Btn></div>
        </form>
      </Card>
      <Card testId="tasks-list-card">
        <CardTitle>All Tasks</CardTitle>
        {items.length === 0 && <Empty>No tasks yet.</Empty>}
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 p-3" data-testid={`task-row-${t.id}`}>
              <div>
                <p className={`text-sm font-black ${t.status === "done" ? "text-dwc-charcoal/40 line-through" : "text-dwc-charcoal"}`}>{t.title} <Badge value={t.status} /></p>
                <p className="text-[11px] font-bold text-dwc-charcoal/55">{t.assigned_name} {t.due_date && `· due ${fmtDate(t.due_date)}`}</p>
              </div>
              <Btn small variant="outline" onClick={() => toggle(t)} testId={`toggle-task-${t.id}`}>{t.status === "done" ? "Reopen" : "Mark Done"}</Btn>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default function OpsDashboard({ user }) {
  const [tab, setTab] = useState("overview");
  const isAdmin = user.role === "admin";
  return (
    <div className="space-y-5" data-testid="ops-dashboard">
      <TabBar prefix="ops" tab={tab} setTab={setTab} tabs={[
        ["overview", "Overview"], ["users", "Users"], ["schedule", "Schedule"],
        ["leaves", "Leaves"], ["incidents", "Incidents"], ["tasks", "Tasks"],
      ]} />
      {!isAdmin && tab === "users" && (
        <p className="rounded-xl bg-amber-50 px-4 py-2 text-[11px] font-bold text-amber-700">
          Service Manager view: you can assign trainers, but only Admin can add, edit or remove users.
        </p>
      )}
      {tab === "overview" && <Overview />}
      {tab === "users" && <Users isAdmin={isAdmin} />}
      {tab === "schedule" && <Schedule />}
      {tab === "leaves" && <Leaves />}
      {tab === "incidents" && <Incidents />}
      {tab === "tasks" && <Tasks />}
    </div>
  );
}
